import { useMemo } from 'react';

// Lower body exercises get +10lb progression; everything else +5lb
const LOWER_BODY = new Set([
  'squat','deadlift','leg press','romanian deadlift','hip thrust',
  'rdl','leg curl','leg extension','hack squat','front squat',
]);
const isLower = (name) => LOWER_BODY.has(name.toLowerCase().trim());

function getStep(name) { return isLower(name) ? 10 : 5; }

function parseTopSets(sets) {
  if (!sets || sets.length === 0) return null;
  const weight = Math.max(...sets.map(s => s.weight || 0));
  const reps   = sets.find(s => s.weight === weight)?.reps || sets[0].reps;
  return { weight, reps, count: sets.length };
}

export function useProgressiveOverload(workouts) {
  return useMemo(() => {
    const strengthSessions = (workouts || [])
      .filter(w => w.exercises?.length > 0)
      .sort((a, b) => b.date.localeCompare(a.date)); // newest first

    // Group by exercise name
    const byExercise = {};
    for (const session of strengthSessions) {
      for (const ex of session.exercises) {
        const key = ex.name.trim();
        if (!byExercise[key]) byExercise[key] = [];
        byExercise[key].push({ date: session.date, sets: ex.sets || [] });
      }
    }

    const result = {};
    for (const [name, sessions] of Object.entries(byExercise)) {
      const history = sessions.slice(0, 5); // last 5
      const last    = sessions[0];
      const prev    = sessions[1];
      const step    = getStep(name);

      let suggestion = null;
      if (last?.sets?.length > 0) {
        const top = parseTopSets(last.sets);
        if (!top) { suggestion = null; }
        else {
          // Check if all sets hit target reps (use last session's reps as target)
          const targetReps = top.reps;
          const allHit = last.sets.every(s => s.reps >= targetReps);

          if (allHit) {
            // Check if two consecutive sessions held same weight
            const prevTop = prev ? parseTopSets(prev.sets) : null;
            const sameAsPrev = prevTop && prevTop.weight === top.weight;
            const add = sameAsPrev ? 2.5 : step;
            suggestion = {
              weight: top.weight + add,
              reps: targetReps,
              sets: last.sets.length,
              reason: sameAsPrev ? '+2.5lb (held weight)' : `+${add}lb`,
            };
          } else {
            // Failed — hold weight
            suggestion = {
              weight: top.weight,
              reps: targetReps,
              sets: last.sets.length,
              reason: 'hold (incomplete reps)',
            };
          }
        }
      }

      // Trend: compare last 3 sessions by top weight
      let trend = 'hold';
      if (sessions.length >= 2) {
        const w0 = parseTopSets(sessions[0]?.sets)?.weight || 0;
        const w1 = parseTopSets(sessions[1]?.sets)?.weight || 0;
        if (w0 > w1) trend = 'up';
        else if (w0 < w1) trend = 'deload';
      }

      result[name] = { lastSession: last, suggestion, history, trend };
    }

    return result;
  }, [workouts]);
}
